import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { Mail, MapPin, Feather, PenLine, Loader2, RefreshCw, ExternalLink, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface FormErrors {
  name?: string;
  city?: string;
  email?: string;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type LoadingState = "idle" | "validating" | "submitting" | "redirecting";

const OrderForm = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loadingState, setLoadingState] = useState<LoadingState>("idle");
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [errorState, setErrorState] = useState<{
    type: "payment" | "network" | "timeout" | null;
    message?: string;
  }>({ type: null });
  const [errors, setErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState({
    name: "",
    city: "",
    email: "",
    letterStyle: "poetic",
    travelFocus: "",
  });

  const isLoading = loadingState !== "idle";

  const validateField = (field: keyof FormErrors, value: string): string | undefined => {
    switch (field) {
      case "name":
        const trimmedName = value.trim();
        if (!trimmedName) return "Please enter your name";
        if (trimmedName.length < 2) return "Name must be at least 2 characters";
        if (trimmedName.length > 100) return "Name must be less than 100 characters";
        return undefined;
      case "city":
        const trimmedCity = value.trim();
        if (!trimmedCity) return "Please enter a city name";
        if (trimmedCity.length < 2) return "City name must be at least 2 characters";
        if (trimmedCity.length > 100) return "City name must be less than 100 characters";
        return undefined;
      case "email":
        const trimmedEmail = value.trim();
        if (!trimmedEmail) return "Please enter your email address";
        if (trimmedEmail.length > 255) return "Email must be less than 255 characters";
        if (!emailRegex.test(trimmedEmail)) return "Please enter a valid email address";
        return undefined;
      default:
        return undefined;
    }
  };

  const validateForm = (): boolean => {
    setLoadingState("validating");
    const newErrors: FormErrors = {
      name: validateField("name", formData.name),
      city: validateField("city", formData.city),
      email: validateField("email", formData.email),
    };
    
    setErrors(newErrors);
    const isValid = !newErrors.name && !newErrors.city && !newErrors.email;
    if (!isValid) {
      setLoadingState("idle");
    }
    return isValid;
  };

  const handleFieldChange = (field: keyof typeof formData, value: string) => {
    setFormData({ ...formData, [field]: value });
    
    // Clear error when user starts typing (only if already submitted)
    if (hasSubmitted && (field === "name" || field === "city" || field === "email")) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
    
    // Clear error state when user makes changes
    if (errorState.type) {
      setErrorState({ type: null });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);
    setErrorState({ type: null });
    
    if (!validateForm()) {
      return;
    }

    setLoadingState("submitting");
    
    // Set timeout for long requests (30 seconds)
    const timeoutId = setTimeout(() => {
      setLoadingState("idle");
      setErrorState({ 
        type: "timeout", 
        message: "Request is taking longer than expected. Please try again." 
      });
    }, 30000);
    
    try {
      const requestBody = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        city: formData.city.trim(),
        letterStyle: formData.letterStyle || "poetic",
        travelFocus: formData.travelFocus || null,
      };
      
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: requestBody,
      });

      clearTimeout(timeoutId);

      if (error) {
        throw new Error(error.message || "Failed to create checkout session");
      }

      if (data?.url && data?.orderId) {
        // Store order ID for the writing page (handles refresh)
        localStorage.setItem("eunice_order_id", data.orderId);
        
        setLoadingState("redirecting");
        // Small delay to show redirecting state
        setTimeout(() => {
          window.location.href = data.url;
        }, 500);
      } else if (data?.url) {
        // Fallback if no orderId returned
        setLoadingState("redirecting");
        setTimeout(() => {
          window.location.href = data.url;
        }, 500);
      } else {
        throw new Error("Unable to create checkout session");
      }
    } catch (error) {
      clearTimeout(timeoutId);
      // Error already captured in errorState - no console logging in production
      const message = error instanceof Error ? error.message : "An unexpected error occurred";
      const isNetworkError = message.includes("fetch") || message.includes("network") || message.includes("Failed to fetch");
      
      setErrorState({ 
        type: isNetworkError ? "network" : "payment", 
        message 
      });
      setLoadingState("idle");
    }
  };

  const handleRetry = () => {
    setErrorState({ type: null });
    handleSubmit({ preventDefault: () => {} } as React.FormEvent);
  };

  const getButtonContent = () => {
    switch (loadingState) {
      case "validating":
        return (
          <>
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Validating...
          </>
        );
      case "submitting":
        return (
          <>
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Creating checkout...
          </>
        );
      case "redirecting":
        return (
          <>
            <ExternalLink className="h-5 w-5 mr-2 animate-pulse" />
            Redirecting to Stripe...
          </>
        );
      default:
        return (
          <>
            Request Your Letter
            <span className="ml-2 text-primary-foreground/80">— $1.99</span>
          </>
        );
    }
  };

  const getErrorMessage = () => {
    switch (errorState.type) {
      case "network":
        return "Network error. Please check your connection and try again.";
      case "timeout":
        return errorState.message || "Request timed out. Please try again.";
      case "payment":
        return errorState.message || "Payment failed. Please try again.";
      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Error banner */}
      {errorState.type && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 text-center animate-fade-up">
          <p className="text-destructive font-sans text-sm mb-3">
            {getErrorMessage()}
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRetry}
              className="border-destructive/50 text-destructive hover:bg-destructive/10"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
            {errorState.type !== "network" && (
              <a 
                href="mailto:letters@eunice.world" 
                className="text-sm text-muted-foreground hover:text-foreground underline"
              >
                Contact support
              </a>
            )}
          </div>
        </div>
      )}

      {/* Name input */}
      <div className="space-y-2">
        <Label htmlFor="name" className="font-serif text-lg flex items-center gap-2">
          <User className="h-4 w-4 text-primary" />
          What's your name, sweetheart?
        </Label>
        <Input
          id="name"
          type="text"
          placeholder="How should Eunice address you?"
          value={formData.name}
          onChange={(e) => handleFieldChange("name", e.target.value)}
          className={cn(
            "h-12 text-base bg-background/50 border-tan/50 focus:border-primary font-sans placeholder:text-muted-foreground/60 disabled:opacity-60 disabled:cursor-not-allowed transition-colors",
            hasSubmitted && errors.name && "border-destructive focus:border-destructive"
          )}
          disabled={isLoading}
          aria-invalid={hasSubmitted && !!errors.name}
          aria-describedby={errors.name ? "name-error" : undefined}
        />
        {hasSubmitted && errors.name && (
          <p id="name-error" className="text-destructive text-sm font-sans animate-fade-up">
            {errors.name}
          </p>
        )}
      </div>

      {/* City input */}
      <div className="space-y-2">
        <Label htmlFor="city" className="font-serif text-lg flex items-center gap-2">
          <MapPin className="h-4 w-4 text-primary" />
          And where are you going?
        </Label>
        <Input
          id="city"
          type="text"
          placeholder="e.g., Paris, Tokyo, Marrakech..."
          value={formData.city}
          onChange={(e) => handleFieldChange("city", e.target.value)}
          className={cn(
            "h-12 text-base bg-background/50 border-tan/50 focus:border-primary font-sans placeholder:text-muted-foreground/60 disabled:opacity-60 disabled:cursor-not-allowed transition-colors",
            hasSubmitted && errors.city && "border-destructive focus:border-destructive"
          )}
          disabled={isLoading}
          aria-invalid={hasSubmitted && !!errors.city}
          aria-describedby={errors.city ? "city-error" : undefined}
        />
        {hasSubmitted && errors.city && (
          <p id="city-error" className="text-destructive text-sm font-sans animate-fade-up">
            {errors.city}
          </p>
        )}
      </div>

      {/* Email input */}
      <div className="space-y-2">
        <Label htmlFor="email" className="font-serif text-lg flex items-center gap-2">
          <Mail className="h-4 w-4 text-primary" />
          Where shall I send you the letter?
        </Label>
        <Input
          id="email"
          type="email"
          placeholder="Your email address"
          value={formData.email}
          onChange={(e) => handleFieldChange("email", e.target.value)}
          className={cn(
            "h-12 text-base bg-background/50 border-tan/50 focus:border-primary font-sans placeholder:text-muted-foreground/60 disabled:opacity-60 disabled:cursor-not-allowed transition-colors",
            hasSubmitted && errors.email && "border-destructive focus:border-destructive"
          )}
          disabled={isLoading}
          aria-invalid={hasSubmitted && !!errors.email}
          aria-describedby={errors.email ? "email-error" : undefined}
        />
        {hasSubmitted && errors.email && (
          <p id="email-error" className="text-destructive text-sm font-sans animate-fade-up">
            {errors.email}
          </p>
        )}
      </div>

      {/* Letter style */}
      <div className="space-y-3">
        <Label className="font-serif text-lg flex items-center gap-2">
          <PenLine className="h-4 w-4 text-primary" />
          Any writing preferences?
        </Label>
        <RadioGroup
          value={formData.letterStyle}
          onValueChange={(value) => handleFieldChange("letterStyle", value)}
          className="flex flex-col sm:flex-row gap-4 sm:gap-6"
          disabled={isLoading}
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="poetic" id="poetic" className="border-tan text-primary min-h-[20px] min-w-[20px]" disabled={isLoading} />
            <Label htmlFor="poetic" className={`font-sans text-base cursor-pointer ${isLoading ? 'opacity-60' : ''}`}>
              Poetic <span className="text-muted-foreground text-sm">(literary)</span>
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="practical" id="practical" className="border-tan text-primary min-h-[20px] min-w-[20px]" disabled={isLoading} />
            <Label htmlFor="practical" className={`font-sans text-base cursor-pointer ${isLoading ? 'opacity-60' : ''}`}>
              Practical <span className="text-muted-foreground text-sm">(detailed)</span>
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Travel focus (optional) */}
      <div className="space-y-2">
        <Label htmlFor="travelStyle" className="font-serif text-lg flex items-center gap-2">
          <Feather className="h-4 w-4 text-primary" />
          Looking for something specific?
          <span className="text-sm text-muted-foreground font-sans">(optional)</span>
        </Label>
        <Select
          value={formData.travelFocus}
          onValueChange={(value) => handleFieldChange("travelFocus", value)}
          disabled={isLoading}
        >
          <SelectTrigger 
            className="h-12 text-base bg-background/50 border-tan/50 focus:border-primary font-sans disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label="Select travel focus preference"
          >
            <SelectValue placeholder="Choose one..." />
          </SelectTrigger>
          <SelectContent className="bg-background border-tan/50">
            <SelectItem value="foodie">Foodie (restaurants & culinary)</SelectItem>
            <SelectItem value="cultural">Cultural (history & traditions)</SelectItem>
            <SelectItem value="off_beaten">Off-beaten (hidden local spots)</SelectItem>
            <SelectItem value="budget">Budget (free & affordable)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Submit button */}
      <Button
        type="submit"
        variant="eunice"
        size="xl"
        className="w-full min-h-[52px]"
        disabled={isLoading}
      >
        {getButtonContent()}
      </Button>

      {/* Trust note */}
      <p className="text-center text-sm text-muted-foreground font-sans">
        Secure payment via Stripe • Letter delivered within 10 minutes
      </p>
    </form>
  );
};

export default OrderForm;
